import React, { forwardRef, useMemo } from 'react'
import { Box, Checkbox, Icon, useMenuButtonBehavior, MenuStateProvider, Menu, MenuItem, InputGroup } from '@maui/elements'

const compareArrayValues = (arr1, arr2) => {
  const compare1 = arr1.every(val => arr2.includes(val))
  const compare2 = arr2.every(val => arr1.includes(val))
  return compare1 && compare2
}

export const SelectMenuButton = forwardRef(({ label, placeholder, name, value, renderInputValue, ...props }, ref) => {
  const menuButtonProps = useMenuButtonBehavior({}, { ...props, ref })

  /*
    Some values may require specific formatting for display in the input.
    For example severity, where the value is all caps: SEVERITY_CRITICAL
  */
  const formattedValue = useMemo(() => {
    if (!value || !value.length) return ''
    if (value.includes('All')) return 'All'
    const renderedValue = renderInputValue ? renderInputValue(value) : value
    return Array.isArray(renderedValue) ? renderedValue.join(', ') : renderedValue
  }, [value, renderInputValue])

  return (
    <Box {...menuButtonProps} width="100%">
      <InputGroup
        slotRight={<Icon name="angle-down" />}
        inputProps={{
          value: formattedValue,
          readOnly: true,
          cursor: 'pointer',
          name,
          placeholder,
          title: formattedValue,
        }}
      />
    </Box>
  )
})

SelectMenuButton.displayName = 'SelectMenuButton'

export const DropdownCheckboxList = forwardRef(({ control, options = [], name, placeholder, renderInputValue }) => {
  const { field: { onChange, value: formValue, ref } } = control
  const optionsValues = options.map(o => o.value)
  const hasAll = formValue.includes('All') || compareArrayValues(optionsValues, formValue)
  const allOptions = ['All', ...options.map(option => option.value)]

  const label = useMemo(() => {
    return options.find(option => option.value === formValue)?.label
  }, [formValue, options])

  const allOptionClicked = () => {
    if (!hasAll) {
      onChange(allOptions)
    } else {
      onChange([])
    }
  }

  const clickHandler = optionValue => () => {
    // If form values contain All
    if (hasAll && optionValue !== 'All') {
      const newValue = [...optionsValues].filter(val => optionValue !== val)
      onChange(newValue)
      return
    }
    // If not all, check or uncheck option
    if (!formValue.includes(optionValue)) {
      formValue.push(optionValue)
    } else {
      formValue.splice(formValue.indexOf(optionValue), 1)
    }
    onChange(formValue)
  }

  return (
    <MenuStateProvider initialState={{ placement: 'bottom-start' }}>
      <SelectMenuButton
        label={label}
        placeholder={placeholder}
        ref={ref}
        name={name}
        value={hasAll ? 'All' : formValue}
        renderInputValue={renderInputValue}
      />
      <Menu width="auto" maxHeight="300px" overflowY={options?.length > 10 && 'scroll'}>
        {
          !options.length &&
          <Box
            color="colorTextMain"
            textStyle="bodySmall"
            width="235px"
            p="spacePaddingLarge"
          >
            No results found
          </Box>
        }
        <MenuItem
          persist
          key="All"
          backgroundColor={hasAll ? 'colorFillMain' : 'transparent'}
        >
          <Checkbox
            name={name}
            value="All"
            checked={hasAll}
            onClick={allOptionClicked}
          >
            <Box width="100%">All</Box>
          </Checkbox>
        </MenuItem>
        {options.map((option, i) => {
          const active = hasAll || formValue.includes(option.value)
          return (
            <MenuItem
              persist
              key={i}
              cursor={formValue === option.value ? 'default' : 'pointer'}
              backgroundColor={active ? 'colorFillMain' : 'transparent'}
            >
              <Checkbox
                name={name}
                value={option.value}
                checked={active}
                onClick={clickHandler(option.value)}
              >
                <Box width="100%">
                  {option.label}
                </Box>
              </Checkbox>
            </MenuItem>
          )
        }
        )}
      </Menu>
    </MenuStateProvider>
  )
})

DropdownCheckboxList.displayName = 'DropDownSelect'
